import { useState } from "react";
import { ContactSupportModal } from "./support/ContactSupportModal";
import EmailSendingSettings from "./EmailSendingSettings";
import BillingSettings from "./BillingSettings";
import { DEFAULT_BRAND_COLOR } from "../lib/brand";
type OSRecord = {
  id: string;
  name: string;
  slug: string;
  brand_color?: string;
  owner_id?: string;
};

type Props = {
  os: OSRecord;
  user: { id: string; email: string } | null;
  userFirstName?: string;
  onSignOut: () => void;
  onOpenIntegrations?: () => void;
};

type SupportIntent = "general" | "delete_os";

export function buildDeleteOsSupportMessage(os: Pick<OSRecord, "id" | "name" | "slug">, userEmail?: string) {
  return [
    `I want to delete the Agentic Operating System "${os.name}" (${os.slug}).`,
    `Agentic Operating System ID: ${os.id}`,
    `Account email: ${userEmail || "unknown"}`,
    "",
    "Please verify ownership, confirm what data will be removed, and tell me the final deletion steps before anything is deleted.",
  ].join("\n");
}

export default function SettingsScreen({ os, user, userFirstName, onSignOut, onOpenIntegrations }: Props) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportIntent, setSupportIntent] = useState<SupportIntent>("general");
  const [accountName, setAccountName] = useState(userFirstName || "");
  const [accountStatus, setAccountStatus] = useState<
    "idle" | "saving" | "saved" | "error" | "rate_limited" | "nothing" | "unavailable"
  >("idle");

  // Update the display name (the A10b endpoint; passwords are gone — sign-in is
  // SloeAuth). An empty field is "nothing to save", never "clear my name".
  const saveAccount = async () => {
    const name = accountName.trim();
    if (!name) {
      setAccountStatus("nothing");
      return;
    }
    const body = { first_name: name };
    setAccountStatus("saving");
    try {
      const res = await fetch("/api/auth/update-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status === 429) { setAccountStatus("rate_limited"); return; }
      if (res.status === 503) { setAccountStatus("unavailable"); return; }
      if (!res.ok) { setAccountStatus("error"); return; }
      setAccountStatus("saved");
    } catch {
      setAccountStatus("error");
    }
  };

  return (
    <div className="px-10 py-8 max-w-4xl">
      <h1 className="text-3xl font-serif text-zinc-900 mb-1">Settings</h1>
      <p className="text-sm text-zinc-400 mb-8">Manage your Agentic Operating System</p>

      <div className="mb-8 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-400">
              System overview
            </div>
            <div className="mt-2 text-lg font-medium text-zinc-900">
              This is the control panel for your account, workspace access, and support.
            </div>
            <div className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-500">
              Use this page to change the owner profile, review billing and email sending,
              open Integrations, or request help without digging through the workspace itself.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-600">
              {os.name}
            </span>
            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-600">
              {os.slug}
            </span>
            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-medium text-zinc-600">
              {os.id.slice(0, 8)}…
            </span>
          </div>
        </div>
      </div>

      {/* Profile */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Profile</h2>
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
              style={{ background: os.brand_color || DEFAULT_BRAND_COLOR }}
            >
              {user?.email?.[0]?.toUpperCase() || "U"}
            </div>
            <div>
              <div className="text-sm font-medium text-zinc-900">{user?.email || "—"}</div>
              <div className="text-xs text-zinc-500">User ID: {user?.id?.slice(0, 8) || "—"}…</div>
            </div>
          </div>
          <button
            onClick={onSignOut}
            className="text-sm font-medium text-zinc-500 hover:text-zinc-900"
          >
            Sign out
          </button>
        </div>
      </section>

      {/* Account */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Account</h2>
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
          <div>
            <label className="block text-sm text-zinc-900 mb-1">Name</label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => { setAccountName(e.target.value); if (accountStatus !== "saving") setAccountStatus("idle"); }}
              placeholder="Your name"
              maxLength={80}
              disabled={accountStatus === "saving"}
              autoComplete="name"
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 disabled:opacity-60"
            />
          </div>
          <p className="text-xs text-zinc-500">
            Sign-in is handled by SloeAuth — Google or a one-time code sent to {user?.email || "your email"}. There is no password to set.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={saveAccount}
              disabled={accountStatus === "saving"}
              className="px-4 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors disabled:opacity-60"
            >
              {accountStatus === "saving" ? "Saving…" : "Save"}
            </button>
            {accountStatus === "saved" && <span className="text-xs text-emerald-600">Saved.</span>}
            {accountStatus === "nothing" && <span className="text-xs text-zinc-500">Enter a name to save.</span>}
            {accountStatus === "unavailable" && <span className="text-xs text-red-600">Names can't be saved yet — try again later.</span>}
            {accountStatus === "rate_limited" && <span className="text-xs text-red-600">Too many attempts. Try again in a few minutes.</span>}
            {accountStatus === "error" && <span className="text-xs text-red-600">Couldn't save. Try again.</span>}
          </div>
        </div>
      </section>

      {/* Email Sending */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Email Sending</h2>
        <EmailSendingSettings osId={os.id} />
      </section>

      {/* Billing */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Billing</h2>
        <BillingSettings osId={os.id} />
      </section>

      {/* Agentic Operating System Info */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Agentic Operating System Info</h2>
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4 text-sm">
          <div className="flex justify-between"><span className="text-zinc-500">Name</span><span className="text-zinc-900">{os.name}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Slug</span><span className="text-zinc-900 font-mono text-xs">{os.slug}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Brand color</span><span className="text-zinc-900 font-mono text-xs">{os.brand_color}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Agentic Operating System ID</span><span className="text-zinc-900 font-mono text-xs">{os.id.slice(0, 12)}…</span></div>
        </div>
      </section>

      {/* Integrations */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Integrations</h2>
        <div className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm text-zinc-900 mb-1">Agent data permissions</div>
            <div className="text-xs text-zinc-500 max-w-lg">
              Connect and repair the approved sources your agents can read, including Gmail where available.
            </div>
          </div>
          {onOpenIntegrations && (
            <button
              type="button"
              onClick={onOpenIntegrations}
              className="px-4 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors shrink-0"
            >
              Open Integrations
            </button>
          )}
        </div>
      </section>

      {/* Help / Support */}
      <section className="mb-8">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-400 mb-3">Help</h2>
        <div className="bg-white border border-zinc-200 rounded-xl p-5">
          <div className="text-sm text-zinc-900 mb-1">Contact support</div>
          <div className="text-xs text-zinc-500 mb-4">
            Have a question, hit a bug, or need help with something? Send us a note — we'll get back to you within 24 hours.
          </div>
          <button
            onClick={() => {
              setSupportIntent("general");
              setSupportOpen(true);
            }}
            className="px-4 py-2 rounded-lg border border-zinc-200 text-zinc-700 text-sm font-medium hover:bg-zinc-50"
          >
            Contact support
          </button>
        </div>
      </section>

      {/* Danger zone */}
      <section>
        <h2 className="text-[11px] font-medium uppercase tracking-[0.16em] text-red-600 mb-3">Danger Zone</h2>
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <div className="text-sm text-zinc-900 mb-1">Delete the Agentic Operating System</div>
          <div className="text-xs text-zinc-500 mb-4">
            Request deletion of the Agentic Operating System and its data. Support verifies ownership and confirms exactly what will be removed before anything is deleted.
          </div>
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50"
            >
              Delete Agentic Operating System
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setConfirmDelete(false);
                  setSupportIntent("delete_os");
                  setSupportOpen(true);
                }}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                Request verified deletion
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 rounded-lg border border-zinc-200 text-zinc-700 text-sm font-medium hover:bg-zinc-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </section>

      <ContactSupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        workspaceId={os.id}
        currentScreen={supportIntent === "delete_os" ? "settings.delete_os" : "settings"}
        title={supportIntent === "delete_os" ? "Request Agentic Operating System deletion" : "Contact Support"}
        description={
          supportIntent === "delete_os"
            ? "For safety, deletion requests go through support verification first. Nothing is removed until ownership and final scope are confirmed."
            : undefined
        }
        initialMessage={
          supportIntent === "delete_os"
            ? buildDeleteOsSupportMessage(os, user?.email)
            : ""
        }
        submitLabel={supportIntent === "delete_os" ? "Send deletion request" : "Send"}
      />
    </div>
  );
}
